import { FormEvent, useEffect, useState } from "react";
import { Heart, LockKeyhole } from "lucide-react";
import { getCountdownParts } from "../countdown";

const WEDDING_START = new Date("2026-12-02T18:30:00+02:00").getTime();

interface Props {
  onLogin: (code: string) => Promise<void>;
}

function formatDateCode(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4, 8)]
    .filter(Boolean)
    .join(".");
}

export function LoginScreen({ onLogin }: Props) {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onLogin(code.trim());
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "הקוד אינו נכון");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-screen">
      <div className="login-photo" aria-hidden="true" />
      <section className="login-panel" aria-labelledby="login-title">
        <Heart className="login-heart" aria-hidden="true" />
        <h1 id="login-title">אבי ורבקה</h1>
        <p>עשרה ימים של אהבה, זיכרונות והפתעות בדרך לחופה.</p>
        <WeddingCountdown />
        <form onSubmit={submit}>
          <label htmlFor="wedding-code">התאריך שלנו</label>
          <div className="input-shell">
            <LockKeyhole aria-hidden="true" />
            <input
              id="wedding-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="DD.MM.YYYY"
              maxLength={10}
              value={code}
              onChange={(event) => setCode(formatDateCode(event.target.value))}
              aria-describedby={error ? "login-error" : undefined}
            />
          </div>
          {error && <p id="login-error" className="form-error" role="alert">{error}</p>}
          <button className="primary-button" type="submit" disabled={busy}>
            {busy ? "פותח…" : "היכנסי לסיפור שלנו"}
          </button>
        </form>
        <p className="login-support">
          משהו לא עובד או לא תקין?{" "}
          <a
            href="https://wa.me/972587127547?text=%D7%94%D7%99%D7%99%20%D7%90%D7%91%D7%99%D7%AA%D7%A8%2C%20%D7%9E%D7%A9%D7%94%D7%95%20%D7%9C%D7%90%20%D7%A2%D7%95%D7%91%D7%93%20%D7%9C%D7%99%20%D7%91%D7%90%D7%AA%D7%A8"
            target="_blank"
            rel="noreferrer"
          >
            שלחי הודעה לאביתר · <bdi>058-712-7547</bdi>
          </a>
        </p>
      </section>
    </main>
  );
}

function WeddingCountdown() {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const remaining = WEDDING_START - now;
  const parts = getCountdownParts(remaining);

  if (remaining <= 0) {
    return (
      <div className="login-wedding-countdown is-complete" role="status">
        <strong>הגיע היום הגדול שלנו</strong>
        <span>אבי ורבקה מתחתנים!</span>
      </div>
    );
  }

  return (
    <section className="login-wedding-countdown" aria-labelledby="wedding-countdown-title">
      <div className="login-countdown-heading">
        <strong id="wedding-countdown-title">עד החתונה</strong>
        <span>02.12.2026 · 18:30 · שעון ישראל</span>
      </div>
      <dl role="timer" aria-label={`נותרו ${parts.days} ימים, ${parts.hours} שעות, ${parts.minutes} דקות ו-${parts.seconds} שניות`}>
        <div><dt>ימים</dt><dd>{parts.days}</dd></div>
        <div><dt>שעות</dt><dd>{String(parts.hours).padStart(2, "0")}</dd></div>
        <div><dt>דקות</dt><dd>{String(parts.minutes).padStart(2, "0")}</dd></div>
        <div><dt>שניות</dt><dd>{String(parts.seconds).padStart(2, "0")}</dd></div>
      </dl>
    </section>
  );
}
